import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../../../../Services/axiosCommon";
import { useNavigate } from "react-router-dom";

const PAGE_SIZE = 10;

const AlertDashboard = () => {
  const navigate = useNavigate();

  const [alerts, setAlerts] = useState([]);       // accumulated list across pages
  const [totalCount, setTotalCount] = useState(0); // total from backend
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchPage = useCallback(async (pageNum, append = false) => {
    pageNum === 1 ? setLoading(true) : setLoadingMore(true);
    try {
      const res = await axiosInstance.get(
        `/dashboard/PR-daysElasped?page=${pageNum}&per_page=${PAGE_SIZE}`
      );
      const pageData = res?.data?.data || [];
      // try to read total from common response shapes
      const total =
        res?.data?.total ??
        res?.data?.count ??
        res?.data?.totalCount ??
        res?.data?.meta?.total ??
        null;

      setAlerts((prev) => (append ? [...prev, ...pageData] : pageData));
      if (total !== null) setTotalCount(total);
      else if (!append) setTotalCount(pageData.length); // fallback: assume no more pages
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchPage(1, false);
  }, [fetchPage]);

  const handleShowMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchPage(nextPage, true); // append results to existing list
  };

  const handleNavigate = (id) => {
    if (id) {
      navigate(`/procurement/purchase-requests/view/${id}`);
    } else {
      navigate(`/procurement/purchase-requests`);
    }
  };

  // Show "Load More" if we know there are more records on the backend
  // If backend returned a total, compare; otherwise use whether last page was full
  const hasMore = totalCount > 0
    ? alerts.length < totalCount
    : alerts.length > 0 && alerts.length % PAGE_SIZE === 0;

  const displayCount = totalCount > 0 ? totalCount : alerts.length;
  const remaining = totalCount > 0 ? totalCount - alerts.length : 0;

  return (
    <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-[0_4px_25px_rgba(0,0,0,0.04)] h-full flex flex-col transition-all duration-300">
      {/* Card Header */}
      <div className="flex items-center justify-between pb-3 border-b border-gray-100 mb-3">
        <div className="flex items-center space-x-2">
          <div className="w-7 h-7 rounded-lg bg-rose-50 flex items-center justify-center text-rose-600">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 className="text-sm font-semibold text-gray-800">Pending YPR Alerts</h3>
        </div>
        <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-rose-50 text-rose-600 border border-rose-100">
          {displayCount} Pending
        </span>
      </div>

      {/* Content Area */}
      {loading ? (
        <div className="p-4 space-y-3 animate-pulse">
          <div className="h-12 bg-gray-100 rounded-xl w-full"></div>
          <div className="h-12 bg-gray-100 rounded-xl w-full"></div>
          <div className="h-12 bg-gray-100 rounded-xl w-full"></div>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto max-h-[250px] pr-1 space-y-2">
          {alerts.length > 0 ? (
            <>
              {alerts.map((item, index) => {
                const days = parseInt(item?.dayElaped || 0);
                const isUrgent = days >= 7;
                const isWarning = days >= 3 && days < 7;

                return (
                  <div
                    key={index}
                    onClick={() => handleNavigate(item?.id)}
                    className="flex items-center justify-between p-3 rounded-xl bg-gray-50/70 hover:bg-rose-50/50 border border-gray-100 hover:border-rose-200/70 cursor-pointer transition-all duration-200 group space-x-3"
                  >
                    <div className="flex items-center space-x-2.5 min-w-0 flex-1">
                      <span
                        className={`w-2 h-2 rounded-full flex-shrink-0 ${
                          isUrgent ? "bg-rose-500 animate-ping" : isWarning ? "bg-amber-500" : "bg-blue-500"
                        }`}
                      ></span>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold text-gray-800 group-hover:text-rose-700 transition-colors truncate">
                          {item?.pr || `PR #${index + 1}`}
                        </p>
                        <p className="text-[10px] text-gray-400 truncate">Tap to review request</p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2 flex-shrink-0">
                      <span
                        className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${
                          isUrgent
                            ? "bg-rose-50 text-rose-700 border-rose-200/50"
                            : isWarning
                            ? "bg-amber-50 text-amber-700 border-amber-200/50"
                            : "bg-blue-50 text-blue-700 border-blue-200/50"
                        }`}
                      >
                        {item?.dayElaped || 0}d elapsed
                      </span>
                      <svg
                        className="w-4 h-4 text-gray-300 group-hover:text-rose-500 group-hover:translate-x-0.5 transition-all flex-shrink-0"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                );
              })}

              {/* Load More Button */}
              {hasMore && (
                <button
                  onClick={handleShowMore}
                  disabled={loadingMore}
                  className="w-full mt-1 py-2 rounded-xl border border-dashed border-rose-200 text-rose-500 text-xs font-semibold hover:bg-rose-50 hover:border-rose-300 transition-all duration-200 flex items-center justify-center gap-1.5 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loadingMore ? (
                    <>
                      <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Loading...
                    </>
                  ) : (
                    <>
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                      Load more
                      {remaining > 0 && (
                        <span className="text-[10px] text-rose-400 font-normal">({remaining} remaining)</span>
                      )}
                    </>
                  )}
                </button>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-500 mb-2">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-xs font-semibold text-gray-700">All PRs Up to Date</p>
              <p className="text-[10px] text-gray-400 mt-0.5">No pending PR alerts required action.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AlertDashboard;
